import type { Money } from 'src/feature/CardDetailHero/cardDetailHeroTypes'
import type {
  StatementPayment,
  StatementSummary,
  StatementTransaction,
} from 'src/page-components/Accounts/AccountStatementsTab/accountStatementsDemoData'
import type {
  CardManagementLocalDate,
  CardManagementNextPaymentDueResponse,
  CardManagementPaymentResponse,
  CardManagementStatementItemResponse,
  CardManagementStatementResponse,
} from 'src/queries/physicalCardManagement/physicalCardManagement.models'

/**
 * Wire → view mapping for the card-detail Statements tab (EX-40600).
 *
 * The Card Management read contracts and this tab's view model disagree in
 * three ways that all have to be reconciled here rather than at a render site:
 *
 * 1. **Dates.** The wire carries date-only fields as `[year, month, day]`
 *    NUMBER ARRAYS, not ISO-8601 strings. The tab's formatters take ISO
 *    strings, so every one is converted on the way in.
 * 2. **Money.** The wire carries a bare integer of minor units alongside a
 *    sibling `currency` field. The tab carries `Money`, and its formatter
 *    scales per currency — pairing the two at the boundary is what stops a
 *    zero-decimal (JPY) or three-decimal (BHD) amount from being mispriced.
 * 3. **Sign.** `statementAmount` and line-item `amount` are SIGNED on the wire
 *    (a credit is negative); `currentAmountDue` and `amountPaid` are not.
 *    Nothing here takes an absolute value — a negative statement really is a
 *    credit and must render as one.
 */

/**
 * `[year, month, day]` → `YYYY-MM-DD`.
 *
 * The wire's `month` is 1-based, so this is a direct pad-and-join rather than
 * anything that goes via `Date` — constructing a `Date` here would apply the
 * runtime's local zone to a value the contract defines in the card's
 * `billingTimeZone`, shifting the rendered day by one for anyone west of UTC.
 */
export const toIsoDate = ([
  year,
  month,
  day,
]: CardManagementLocalDate): string =>
  `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(
    day,
  ).padStart(2, '0')}`

/** Pairs a wire amount with its sibling `currency` field. */
export const toMoney = (amountMinor: number, currency: string): Money => ({
  amountMinor,
  currency,
})

/**
 * The wire's four-value status vocabulary passes through unchanged.
 *
 * It is deliberately NOT collapsed to the demo's original `OPEN | PAID`:
 * `PAST_DUE` and `PARTIALLY_PAID` are distinctions the server makes on the
 * user's behalf — the contract is explicit that the server, not the client
 * clock, decides when an obligation crosses into `PAST_DUE` — and flattening
 * them would render an overdue balance as merely open.
 */
export const toStatementSummary = (
  statement: CardManagementStatementResponse,
): StatementSummary => ({
  id: statement.id,
  // The period label renders as "June 2026", so the period START is the
  // meaningful anchor; `periodEnd` would land on the same month for a normal
  // cycle but not for one that straddles a month boundary.
  period: toIsoDate(statement.periodStart),
  dueOn: toIsoDate(statement.dueDate),
  status: statement.status,
  statementAmount: toMoney(statement.statementAmount, statement.currency),
  // `currentAmountDue` — what is still unresolved after payments, adjustments,
  // and reversals — not `amountOriginallyDue`. The row reads "Owed X", which is
  // a present-tense claim about what the user still has to settle.
  amountOwed: toMoney(statement.currentAmountDue, statement.currency),
  // Always present on the wire (`0` when nothing has been applied), optional in
  // the view model. Mapping a real `0` to `undefined` would make the row fall
  // back to showing the owed amount as if it had been paid, so only the
  // absence of any payment is dropped.
  ...(statement.amountPaid > 0
    ? { amountPaid: toMoney(statement.amountPaid, statement.currency) }
    : {}),
  // Passed through verbatim: the API-owned URL is the complete delivery
  // contract. `?? {}` is a defensive reading for a response predating the
  // field; no configured formats is safer than constructing a location.
  documentUrls: statement.documentUrls ?? {},
})

/**
 * Line items arrive in ISSUER PRESENTATION order (`sequence ASC`) and are
 * deliberately not re-sorted by date — the contract is explicit that the list
 * mirrors the printed statement, so a client re-sort would put our ledger out
 * of step with the document the user can download.
 */
export const toStatementTransaction = (
  item: CardManagementStatementItemResponse,
): StatementTransaction => ({
  id: item.id,
  postedOn: toIsoDate(item.postedOn),
  description: item.description,
  amount: toMoney(item.amount, item.currency),
})

export const toStatementPayment = (
  payment: CardManagementPaymentResponse,
): StatementPayment => ({
  id: payment.id,
  amount: toMoney(payment.amount, payment.currency),
  paidOn: new Date(payment.occurredAt).toISOString(),
  method: payment.type,
  status: payment.status,
})

/**
 * The next-payment figure the Payments section leads with.
 *
 * Preserves `null` when the issuer supplied no usable pair. The caller keeps
 * that distinct from an unavailable query but renders neither as a financial
 * claim.
 */
export const toNextPaymentDue = (
  nextPaymentDue: CardManagementNextPaymentDueResponse | null,
): { amount: Money; dueOn: string; isOverdue: boolean } | null =>
  nextPaymentDue === null
    ? null
    : {
        amount: toMoney(nextPaymentDue.amountDue, nextPaymentDue.currency),
        dueOn: toIsoDate(nextPaymentDue.dueDate),
        // Taken from the server's `status`, never derived by comparing
        // `dueDate` to the browser clock: the boundary is a billing-calendar
        // date in the card's own timezone, which the client does not own.
        // PAST_DUE is the currently deployed API value. Keep it through the
        // additive rollout; remove it after the expanded API is fully deployed.
        isOverdue:
          nextPaymentDue.status === 'OVERDUE' ||
          nextPaymentDue.status === 'PAST_DUE',
      }
